import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '@nebalkon-workspace/auth';
import { CabinetShellChromeService } from '@nebalkon-workspace/cabinet-client';
import { CABINET_PATHS, CABINET_PLATFORM } from '@nebalkon-workspace/cabinet-core';
import {
  buildStaffCabinetMobileBottomTabs,
  isStaffCabinetNavActive,
  isStaffCabinetTabRoot,
  resolveStaffCabinetBackRouteFromUrl,
  shouldHideStaffCabinetPageHeader,
  staffCabinetNavLink,
  type StaffCabinetMobileTabItem,
} from '../navigation/staff-cabinet-nav.util';
import { StaffQrScanFlowService } from '../scan/staff-qr-scan-flow.service';
import { CABINET_STAFF_SHELL_SHOW_WEB_HEADER } from './cabinet-staff-shell-web-header.token';
import { MOBILE_STAFF_CABINET_SHELL_PROVIDERS } from './staff-cabinet-shell.providers';

@Component({
  selector: 'lib-cabinet-staff-shell',
  imports: [RouterOutlet, RouterLink, TranslatePipe],
  providers: [...MOBILE_STAFF_CABINET_SHELL_PROVIDERS],
  templateUrl: './cabinet-staff-shell.component.html',
  styleUrl: './cabinet-staff-shell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CabinetStaffShellComponent {
  readonly #router = inject(Router);
  readonly #auth = inject(AuthService);
  readonly #paths = inject(CABINET_PATHS);
  readonly #platform = inject(CABINET_PLATFORM);
  readonly #qrScanFlow = inject(StaffQrScanFlowService);
  readonly #shellChrome = inject(CabinetShellChromeService);

  protected readonly showWebHeader = inject(CABINET_STAFF_SHELL_SHOW_WEB_HEADER);
  protected readonly isMobileApp = this.#platform === 'mobile';

  protected readonly bottomTabsReplaced = this.#shellChrome.bottomTabsReplaced;

  readonly #currentUrl = toSignal(
    this.#router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.#router.url),
    ),
    { initialValue: this.#router.url },
  );

  protected readonly bottomTabs = computed(() =>
    buildStaffCabinetMobileBottomTabs(this.#auth.roles()),
  );

  protected readonly showBottomTabs = computed(() => this.bottomTabs().length > 0);

  protected readonly hasQrTab = computed(() =>
    this.bottomTabs().some((item) => item.type === 'qr'),
  );

  readonly #usesMobileBackLayout = computed(
    () => this.isMobileApp || !this.showWebHeader,
  );

  constructor() {
    effect(() => {
      const url = this.#currentUrl();
      const mobileLayout = this.#usesMobileBackLayout();

      this.#shellChrome.setHidePageHeaderOnTabRoot(
        shouldHideStaffCabinetPageHeader(url, this.#paths),
      );

      if (isStaffCabinetTabRoot(url, this.#paths)) {
        this.#shellChrome.setBackRoute(null);
        this.#shellChrome.setUseHistoryBack(false);
        return;
      }

      this.#shellChrome.setBackRoute(
        resolveStaffCabinetBackRouteFromUrl(url, this.#paths, { mobileLayout }),
      );
      this.#shellChrome.setUseHistoryBack(false);
    });
  }

  protected navLink(route: string): string {
    return staffCabinetNavLink(route, this.#paths);
  }

  protected isNavActive(route: string, exact?: boolean): boolean {
    return isStaffCabinetNavActive(
      this.#currentUrl(),
      {
        labelKey: '',
        route,
        exact,
        roles: [],
      },
      this.#paths,
    );
  }

  protected tabTrackKey(item: StaffCabinetMobileTabItem, index: number): string {
    return item.type === 'qr' ? 'qr' : item.route || `nav-${index}`;
  }

  protected onQrScan(): void {
    void this.#qrScanFlow.scanAndNavigateToResult({ fallbackToManualScan: true });
  }
}
